import React, { JSX } from 'react';
import MuiButton from '@mui/material/Button';
import { CommonPropsAndTypes } from '../../_Types/StandardComponentProps';
import EDTooltip from '../../Tooltip/EDTooltip';
import EDTypography from '../../Typography/EDTypography';
import { Box, styled, SxProps } from '@mui/material';
import { spacing, SpacingProps } from '@mui/system';
import classNames from 'classnames';
import {
  useIsExtraSmallScreen,
  useIsSmallScreen,
} from '../../../Hooks/useFullscreenView';

export interface IProps extends SpacingProps {
  label?: string | JSX.Element;
  shortLabel?: string;
  captionLabel?: boolean;
  disabled?: boolean;
  disabledReason?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  primary?: boolean;
  flat?: boolean;
  compressed?: boolean;
  fullWidth?: boolean;
  alignLeft?: boolean;
  size?: CommonPropsAndTypes.StandardSizesType;
  title?: string;
  icon?: any;
  color?: CommonPropsAndTypes.ColorType;
  labelBold?: boolean;
  labelNotUpperCase?: boolean;
  buttonRef?: React.Ref<any> | React.RefObject<any>;
  lowEmphasis?: boolean;
  testId?: string;
  smartPhoneActionButton?: boolean;
  component?: any;
  tooltip?: string;
  children?: React.ReactNode;
  resourceBtnStyling?: boolean;
  alignIconToEnd?: boolean;
  outlined?: boolean;
  wordBreak?: boolean;
}

const stretchyStyle: SxProps = {
  flex: '1 0 auto',
  wordBreak: 'break-word',
  '& > .stretchy': {
    width: `calc(100% - 16px)`, // TODO: bevaka när 'fill-available' övergår i stretch, då är det ett bättre alternativ
  },
};
const resourceTextBtn: SxProps = {
  flex: '1 0 auto',
  width: '150px',
};
const resourceNoTextBtn: SxProps = {
  flex: '1 0 auto',
  width: '30px',
  minWidth: '30px',
};

/**
 * Normal knapp, label, färg och utseende kan styras genom props.
 */
export const EDButtonBase: React.FC<
  React.PropsWithChildren<IProps & { className?: string }>
> = ({ buttonRef, ...props }) => {
  const isSmallScreen = useIsSmallScreen();
  const isExtraSmallScreen = useIsExtraSmallScreen();

  const color: CommonPropsAndTypes.ColorType | undefined = props.primary
    ? 'primary'
    : props.color != null
    ? props.color
    : undefined;

  const component = (
    <MuiButton
      disabled={props.disabled}
      onClick={props.onClick}
      color={color}
      variant={
        props.flat ? undefined : props.outlined ? 'outlined' : 'contained'
      }
      sx={[
        props?.wordBreak === false
          ? { wordBreak: 'normal' }
          : { wordBreak: 'break-word' },
        Boolean(props.lowEmphasis) && { fontWeight: 400, letterSpacing: 0 },
        props.smartPhoneActionButton
          ? stretchyStyle
          : props.resourceBtnStyling
          ? isSmallScreen
            ? resourceNoTextBtn
            : resourceTextBtn
          : {
              width: props.compressed
                ? 'min-content'
                : props.fullWidth
                ? '100%'
                : 'auto',
            },
        Boolean(props.alignLeft) && { justifyContent: 'flex-start' },
        Boolean(props.alignIconToEnd) && { flexDirection: 'row-reverse' },
        Boolean(props.labelBold) && { fontWeight: 900 },
        Boolean(props.labelNotUpperCase) && { textTransform: 'none' },
        Boolean(props.captionLabel) && {
          display: 'flex',
          flexDirection: 'column',
        },
      ]}
      size={props.size}
      title={props.title}
      ref={buttonRef}
      data-testid={props.testId}
      component={props.component}
      className={classNames(props.className, {
        stretchy: props.smartPhoneActionButton,
      })}
    >
      {props.icon && (
        <Box
          sx={[
            !props.captionLabel && {
              marginBottom: 0.25,
            },
          ]}
        >
          {props.icon}
        </Box>
      )}
      {props.captionLabel && (
        <>
          <EDTypography
            variant="caption"
            style={{ textAlign: 'center', display: 'inherit' }}
            color="inherit"
            disableLetterSpacing
            breakWord
          >
            {props.label}
          </EDTypography>
        </>
      )}
      {!props.captionLabel && !props.shortLabel && <>{props.label}</>}
      {!props.captionLabel && props.shortLabel && (
        <>{isExtraSmallScreen ? props.shortLabel : props.label}</>
      )}
      {props.children}
    </MuiButton>
  );

  if ((props.disabled && props.disabledReason) || props.tooltip) {
    return (
      <EDTooltip
        title={props.disabled ? props.disabledReason : props.tooltip}
        enableClickOnTouch={props.disabled}
      >
        <Box
          component={'span'}
          sx={[
            { overflow: 'hidden' },
            Boolean(props.smartPhoneActionButton) && stretchyStyle,
          ]}
        >
          {component}
        </Box>
      </EDTooltip>
    );
  }

  return component;
};

const EDButton: React.FC<React.PropsWithChildren<IProps>> = styled(
  EDButtonBase,
  { name: 'EDButton' }
)(spacing);
export default EDButton;
